import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { useAuthStore } from '@/stores/authStore';
import type { AuthErrorPayload } from '@/api/auth.types';

const schema = z.object({
  email: z.string().min(1, 'E-mail requis').email('E-mail invalide'),
  password: z.string().min(1, 'Mot de passe requis'),
});

type LoginFormValues = z.infer<typeof schema>;

interface LocationState {
  from?: { pathname?: string };
}

export default function LoginPage(): JSX.Element {
  const phase = useAuthStore((s) => s.phase);
  const login = useAuthStore((s) => s.login);
  const location = useLocation();
  const [submitError, setSubmitError] = useState<AuthErrorPayload | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  if (phase === 'authenticated') {
    const state = location.state as LocationState | null;
    const target = state?.from?.pathname ?? '/dashboard';
    return <Navigate to={target} replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await login(values.email, values.password);
    } catch (err) {
      // `login` rejette l'erreur normalisée.
      const payload = err as AuthErrorPayload;
      setSubmitError(payload);
    }
  });

  return (
    <div className="grid min-h-screen grid-cols-1 bg-pearl lg:grid-cols-[1.05fr_1fr]">
      {/* Colonne marketing — Navy gradient */}
      <aside
        aria-hidden
        className="hidden flex-col justify-center bg-gradient-to-br from-navy-600 to-navy-900 px-14 py-14 text-white lg:flex"
      >
        <div className="flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-lg bg-gold-500 font-display text-2xl font-black text-navy-700">
            H
          </div>
          <span className="font-display text-2xl font-extrabold tracking-tightish text-gold-300">
            Harmaury
          </span>
        </div>
        <h1 className="mt-10 max-w-md font-display text-4xl font-black leading-tight tracking-tightish">
          Console de gestion Harmaury
        </h1>
        <p className="mt-4 max-w-md font-sans text-base leading-relaxed text-white/70">
          Pilotez le personnel, les devis, la facturation, la CNPS et la notation depuis un poste unique.
        </p>
        <ul className="mt-8 flex flex-col gap-3 font-sans text-sm text-white/85">
          {[
            'Tableau de bord temps réel',
            'Devis & contrats automatisés',
            'Notation & primes du mois',
          ].map((label) => (
            <li key={label} className="flex items-center gap-2">
              <span
                aria-hidden
                className="grid h-5 w-5 place-items-center rounded-full bg-gold-500/20 text-gold-300"
              >
                <svg viewBox="0 0 20 20" className="h-3 w-3" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M16.7 5.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-3-3a1 1 0 011.4-1.4L9 11.6l6.3-6.3a1 1 0 011.4 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
              {label}
            </li>
          ))}
        </ul>
      </aside>

      {/* Colonne formulaire */}
      <main className="flex items-center justify-center px-6 py-12 lg:px-12">
        <div className="w-full max-w-sm">
          {/* Header mobile (logo) */}
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-gold-500 font-display text-xl font-black text-navy-700">
              H
            </div>
            <span className="font-display text-xl font-extrabold text-navy-700">Harmaury</span>
          </div>
          <h2 className="font-display text-2xl font-black text-navy-700">Connexion</h2>
          <p className="mt-1 font-sans text-sm text-sub">
            Accédez à votre console d&rsquo;administration.
          </p>

          <form noValidate className="mt-8 flex flex-col gap-4" onSubmit={onSubmit}>
            <div>
              <label
                htmlFor="email"
                className="block font-display text-xs font-bold uppercase tracking-wider text-sub"
              >
                E-mail professionnel
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                spellCheck={false}
                aria-invalid={errors.email !== undefined}
                aria-describedby={errors.email !== undefined ? 'email-error' : undefined}
                className="mt-2 w-full rounded-xl border border-line bg-white px-4 py-3 font-sans text-sm text-ink shadow-soft focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500/30"
                placeholder="vous@harmaury.com"
                {...register('email')}
              />
              {errors.email !== undefined && (
                <p id="email-error" role="alert" className="mt-1 font-sans text-xs text-danger">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block font-display text-xs font-bold uppercase tracking-wider text-sub"
              >
                Mot de passe
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={errors.password !== undefined}
                aria-describedby={errors.password !== undefined ? 'password-error' : undefined}
                className="mt-2 w-full rounded-xl border border-line bg-white px-4 py-3 font-sans text-sm text-ink shadow-soft focus:border-gold-500 focus:outline-none focus:ring-2 focus:ring-gold-500/30"
                placeholder="••••••••"
                {...register('password')}
              />
              {errors.password !== undefined && (
                <p id="password-error" role="alert" className="mt-1 font-sans text-xs text-danger">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="text-right">
              <Link
                to="/forgot"
                className="font-display text-xs font-semibold text-gold-700 hover:text-gold-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500"
              >
                Mot de passe oublié ?
              </Link>
            </div>

            {submitError !== null && (
              <div
                role="alert"
                className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 font-sans text-sm text-danger"
              >
                {submitError.message}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl bg-gold-500 px-5 py-3 font-display text-sm font-extrabold text-navy-700 shadow-gold transition hover:bg-gold-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSubmitting ? 'Connexion…' : 'Se connecter'}
            </button>
          </form>

          <p className="mt-6 text-center font-sans text-xs text-sub">
            Accès réservé au personnel autorisé · 2FA bientôt activée
          </p>
        </div>
      </main>
    </div>
  );
}
